<template>
  <v-dialog
    v-if="editedEvent"
    :value="dialog"
    :max-width="550"
    persistent
    :retain-focus="false"
    scrollable
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined class="p-events-edit-dialog">
      <v-card-title class="pb-0 pt-3">
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-card-text class="px-0 pb-2">
        <v-form ref="form" @submit.stop.prevent>
          <div class="p-events-edit-dialog__section">
            <v-text-field
              type="text"
              v-model.trim="editedEvent.title"
              :rules="rules.title"
              :placeholder="$t('event.edit.title')"
              class="p-input-lg p-clearable-on-hover pt-0 ml-10"
              tabindex="1"
              autofocus
              clearable
            ></v-text-field>
            <div class="d-flex align-center justify-start" :class="{ 'p-event-edit-dialog-all-day': editedEvent.allDay }">
              <p-date-time-picker
                v-model="editedEvent.start"
                :date-only="editedEvent.allDay"
                prepend-icon="mdi-clock-outline"
                date-class="p-input-implicit"
                time-class="p-input-implicit"
                hide-details="auto"
                class="flex-grow-0"
                tabindex="1"
                dense
                implicit
                center
                @change="onStartDateChange"
              ></p-date-time-picker>
              <span class="mx-2 pt-1">-</span>
              <p-date-time-picker
                v-model="editedEvent.stop"
                :date-only="editedEvent.allDay"
                date-class="p-input-implicit"
                time-class="p-input-implicit"
                hide-details="auto"
                class="flex-grow-0"
                tabindex="1"
                reversed
                dense
                implicit
                center
              ></p-date-time-picker>
            </div>
            <div
              v-if="dateRangeError"
              v-text="dateRangeError"
              class="v-messages error--text ml-10"
            ></div>
            <v-checkbox
              v-model="editedEvent.allDay"
              :label="$t('event.edit.allDay')"
              class="mt-1 ml-11"
              tabindex="1"
              hide-details
              dense
            ></v-checkbox>
            <v-select
              v-model="editedEvent.categoryId"
              :items="eventCategories"
              :rules="rules.categoryId"
              :placeholder="$t('event.edit.category')"
              prepend-icon="mdi-folder-outline"
              item-value="id"
              item-text="name"
              hide-details="auto"
              class="p-input-implicit mt-3"
              tabindex="1"
              dense
            ></v-select>
            <p-google-places-autocomplete
              :google-place.sync="editedEvent.googlePlace"
              :google-place-id.sync="editedEvent.googlePlaceId"
              :placeholder="$t('event.edit.location')"
              prepend-icon="mdi-map-marker-outline"
              hide-details="auto"
              class="p-input-implicit p-clearable-on-hover mt-2"
              tabindex="1"
              clearable
              dense
            ></p-google-places-autocomplete>
            <v-textarea
              type="text"
              v-model.trim="editedEvent.note"
              :placeholder="$t('event.edit.note')"
              :rows="1"
              prepend-icon="mdi-text"
              class="p-input-implicit p-clearable-on-hover mt-2"
              tabindex="1"
              clearable
              auto-grow
              dense
            ></v-textarea>
          </div>

          <v-divider class="mb-5"></v-divider>

          <div class="p-events-edit-dialog__section">
            <div class="p-events-edit-dialog__members-title">{{ $t('event.edit.members') }}</div>
              <members-select-list
                v-model="editedEvent.members"
                :placeholder="$t('event.edit.addMember')"
                prepend-icon="mdi-account-multiple-outline"
                hide-details="auto"
                input-class="p-input-implicit"
                tabindex="1"
                dense
              ></members-select-list>
          </div>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-center">
        <v-btn text tabindex="3" @click="moreOptions">{{ $t('moreOptions') }}</v-btn>
        <v-btn text color="primary" tabindex="2" :disabled="!isModelChanged()" @click="submit">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import { dateTimeFormats } from '@/constants'

import moment from 'moment'

import PDateTimePicker from '@/components/PDateTimePicker'
import PGooglePlacesAutocomplete from '@/components/PGooglePlacesAutocomplete'
import PBtnIcon from '@/components/PBtnIcon'

import MembersSelectList from './MembersSelectList'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


export default {
  components: {
    PDateTimePicker,
    PGooglePlacesAutocomplete,
    PBtnIcon,
    MembersSelectList
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    event: Object
  },


  data () {
    return {
      editedEvent: null,
      defaultEvent: {
        title: '',
        note: '',
        start: null,
        stop: null,
        allDay: false,
        rRule: null,
        categoryId: null,
        googlePlace: null,
        googlePlaceId: null,
        labels: [],
        members: [],
        statusId: 0,
        isPromoter: false
      }
    }
  },


  computed: {
    ...mapGetters('eventCategory', ['eventCategories']),
    ...mapGetters('event', ['lastEventDate']),
    ...mapGetters('project', ['currentProjectId']),

    _lastEventDate () {
      return this.lastEventDate(this.currentProjectId)
    },

    dialogTitle () {
      return this.event ? this.$t('event.editEvent') : this.$t('event.createEvent')
    },

    dateRangeError () {
      const isValid = moment(this.editedEvent.start).isSameOrBefore(this.editedEvent.stop)
      return isValid ? false : this.$t('validation.eventDateRange')
    },

    rulesToApply () {
      const rule = this.rule

      return {
        title: [rule.required()],
        categoryId: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('event', ['createEvent', 'updateEvent']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.event) {
        this.removeFromChanges(this.event)
      }
      this.initData()
      this.initWatchedModel(this.editedEvent)
      this.resetValidation()
    },

    initData () {
      const event = this.getObjectCopy(this.event || this.defaultEvent)

      // event.start = event.start ?? moment.utc().add(1, 'h').minute(0).second(0).format(dateTimeFormats.dateTimeUtc)
      event.start = event.start ?? this._lastEventDate ? moment.utc(this._lastEventDate).format(dateTimeFormats.dateTimeUtc) : moment.utc().add(1, 'h').minute(0).second(0).format(dateTimeFormats.dateTimeUtc)
      event.stop = event.stop ?? moment.utc(event.start).add(1, 'h').minute(0).second(0).format(dateTimeFormats.dateTimeUtc)

      if (!event.categoryId && this.eventCategories.length > 0) {
        event.categoryId = this.eventCategories[0].id
      }

      this.editedEvent = event
    },

    onStartDateChange () {
      if (moment.utc(this.editedEvent.stop).isBefore(this.editedEvent.start)) {
        this.editedEvent.stop = moment.utc(this.editedEvent.start).add(1, 'h').format(dateTimeFormats.dateTimeUtc)
      }
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged() || this.dateRangeError) return

      try {
        this.showSnackbarProcessing()

        const event = this.getObjectCopy(this.editedEvent)

        if (event.id) {
          await this.updateEvent(event)
          this.showSnackbarSuccess(this.$t('event.eventUpdated'))
        } else {
          event.projectId = this.currentProjectId
          await this.createEvent(event)
          this.showSnackbarSuccess(this.$t('event.eventCreated'))
        }

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    moreOptions () {
      this.closeDialog()
      this.$emit('more-options', this.editedEvent)
    }
  }
}
</script>
